import { useState, useEffect, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, MoreHorizontal, Pencil, Plus, Trash2 } from "lucide-react";
import type { DiscountCodeSchemaType, DiscountCodeType, DiscountCodeStatus } from "@/models/DiscountCode";

interface DiscountCodeEntry {
  code: string;
  usageCount: number;
}

interface DiscountCode {
  _id: string;
  title: string;
  status: DiscountCodeStatus;
  codes: DiscountCodeEntry[];
  discount: {
    type: DiscountCodeType;
    value: number;
    target: "ENTIRE_ORDER";
  };
  createdAt: string;
}

const emptyForm = () => ({
  title: "",
  status: "ACTIVE" as DiscountCodeStatus,
  codesRaw: "",
  discountType: "PERCENTAGE" as DiscountCodeType,
  discountValue: "",
});

function parseCodesRaw(raw: string): DiscountCodeEntry[] {
  return raw
    .split(/[\n,]+/)
    .map((s) => s.trim().toUpperCase())
    .filter(Boolean)
    .map((code) => ({ code, usageCount: 0 }));
}

function codesToRaw(codes: DiscountCodeEntry[]): string {
  return codes.map((c) => c.code).join("\n");
}

export function Discounts() {
  const [discountCodes, setDiscountCodes] = useState<DiscountCode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const hasLoadedOnce = useRef(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [isSaving, setIsSaving] = useState(false);

  const [pendingDelete, setPendingDelete] = useState<DiscountCode | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchDiscountCodes();
  }, []);

  async function fetchDiscountCodes() {
    setIsLoading(true);
    try {
      const res = await fetch("/api/discount-codes");
      if (res.ok) {
        const data = await res.json();
        setDiscountCodes(data.discountCodes ?? []);
      }
    } catch (error) {
      console.error("Failed to fetch discount codes:", error);
    } finally {
      setIsLoading(false);
      hasLoadedOnce.current = true;
    }
  }

  function openCreate() {
    setEditingId(null);
    setForm(emptyForm());
    setDialogOpen(true);
  }

  function openEdit(dc: DiscountCode) {
    setEditingId(dc._id);
    setForm({
      title: dc.title,
      status: dc.status,
      codesRaw: codesToRaw(dc.codes),
      discountType: dc.discount.type,
      discountValue: String(dc.discount.value),
    });
    setDialogOpen(true);
  }

  async function handleSave() {
    const value = parseFloat(form.discountValue);
    if (!form.title.trim() || isNaN(value) || value <= 0) return;

    setIsSaving(true);
    try {
      const codes = parseCodesRaw(form.codesRaw);
      const payload = {
        title: form.title.trim(),
        status: form.status,
        codes,
        discount: { type: form.discountType, value, target: "ENTIRE_ORDER" as const },
      };

      const url = editingId ? `/api/discount-codes/${editingId}` : "/api/discount-codes";
      const method = editingId ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        const data = await res.json();
        const saved = data.discountCode as DiscountCode;
        setDiscountCodes((prev) =>
          editingId
            ? prev.map((d) => (d._id === editingId ? saved : d))
            : [saved, ...prev],
        );
        setDialogOpen(false);
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.error || "Failed to save discount code");
      }
    } catch (error) {
      console.error("Failed to save discount code:", error);
      alert("An error occurred");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete() {
    if (!pendingDelete) return;
    setIsDeleting(true);
    try {
      const res = await fetch(`/api/discount-codes/${pendingDelete._id}`, { method: "DELETE" });
      if (res.ok) {
        setDiscountCodes((prev) => prev.filter((d) => d._id !== pendingDelete._id));
        setPendingDelete(null);
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.error || "Failed to delete discount code");
      }
    } catch (error) {
      console.error("Failed to delete discount code:", error);
      alert("An error occurred");
    } finally {
      setIsDeleting(false);
    }
  }

  function formatDiscount(dc: DiscountCode) {
    if (dc.discount.type === "PERCENTAGE") return `${dc.discount.value}%`;
    return `$${dc.discount.value.toFixed(2)}`;
  }

  function formatDate(dateString: string) {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }

  if (isLoading && !hasLoadedOnce.current) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-xl font-semibold">Discount Codes</h1>
        <Button size="sm" onClick={openCreate}>
          <Plus className="h-4 w-4 mr-1" />
          Create
        </Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Title</TableHead>
            <TableHead>Codes</TableHead>
            <TableHead>Discount</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="hidden lg:table-cell">Created</TableHead>
            <TableHead className="w-[52px]" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={6} className="h-24 text-center">
                <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
              </TableCell>
            </TableRow>
          ) : discountCodes.length > 0 ? (
            discountCodes.map((dc) => (
              <TableRow key={dc._id}>
                <TableCell className="font-medium">{dc.title}</TableCell>
                <TableCell className="text-sm">
                  {dc.codes.length === 0 ? (
                    <span className="text-muted-foreground">—</span>
                  ) : (
                    <div className="flex flex-col gap-0.5">
                      {dc.codes.map((entry) => (
                        <span key={entry.code}>
                          <span className="font-mono">{entry.code}</span>
                          {entry.usageCount > 0 && (
                            <span className="text-muted-foreground ml-1">
                              ×{entry.usageCount}
                            </span>
                          )}
                        </span>
                      ))}
                    </div>
                  )}
                </TableCell>
                <TableCell>{formatDiscount(dc)}</TableCell>
                <TableCell>
                  <span
                    className={
                      dc.status === "ACTIVE"
                        ? "text-green-600 font-medium"
                        : "text-muted-foreground"
                    }
                  >
                    {dc.status === "ACTIVE" ? "Active" : "Inactive"}
                  </span>
                </TableCell>
                <TableCell className="hidden lg:table-cell">{formatDate(dc.createdAt)}</TableCell>
                <TableCell className="p-2" onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Discount code actions">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEdit(dc)}>
                        <Pencil className="h-4 w-4" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem variant="destructive" onClick={() => setPendingDelete(dc)}>
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={6} className="h-24 text-center">
                No discount codes yet.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!isSaving) setDialogOpen(open); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit discount code" : "Create discount code"}</DialogTitle>
            <DialogDescription>
              {editingId ? "Update the discount code details." : "Add a new discount code for your checkout."}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-4 py-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="dc-title">Title</Label>
              <Input
                id="dc-title"
                placeholder="e.g. Summer Sale"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="dc-codes">Codes (one per line or comma-separated)</Label>
              <textarea
                id="dc-codes"
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 font-mono uppercase"
                placeholder={"SUMMER10\nFALL20"}
                value={form.codesRaw}
                onChange={(e) => setForm((f) => ({ ...f, codesRaw: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="dc-type">Type</Label>
                <Select
                  value={form.discountType}
                  onValueChange={(v) => setForm((f) => ({ ...f, discountType: v as DiscountCodeType }))}
                >
                  <SelectTrigger id="dc-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PERCENTAGE">Percentage (%)</SelectItem>
                    <SelectItem value="FIXED">Fixed ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="dc-value">
                  {form.discountType === "PERCENTAGE" ? "Percent off" : "Amount off ($)"}
                </Label>
                <Input
                  id="dc-value"
                  type="number"
                  min={0}
                  step={form.discountType === "PERCENTAGE" ? 1 : 0.01}
                  placeholder={form.discountType === "PERCENTAGE" ? "e.g. 15" : "e.g. 5.00"}
                  value={form.discountValue}
                  onChange={(e) => setForm((f) => ({ ...f, discountValue: e.target.value }))}
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="dc-status">Status</Label>
              <Select
                value={form.status}
                onValueChange={(v) => setForm((f) => ({ ...f, status: v as DiscountCodeStatus }))}
              >
                <SelectTrigger id="dc-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ACTIVE">Active</SelectItem>
                  <SelectItem value="INACTIVE">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              onClick={() => void handleSave()}
              disabled={isSaving || !form.title.trim() || !form.discountValue}
            >
              {isSaving ? "Saving..." : editingId ? "Save changes" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={pendingDelete !== null}
        onOpenChange={(open) => { if (!open) setPendingDelete(null); }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this discount code?</DialogTitle>
            <DialogDescription>
              {pendingDelete
                ? `This will permanently remove "${pendingDelete.title}". This cannot be undone.`
                : "This cannot be undone."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setPendingDelete(null)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => void handleDelete()} disabled={isDeleting}>
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
